import React, { Component, useState, useEffect, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import axios from "axios";

import { Layout, Row, Col, Table, message } from "antd";
import moment from "moment-timezone";
import numberFormat from "./../../utils/numberFormat";

import { sumBy, uniq } from "lodash";

import ReportHeading from "../../utils/ReportHeading";
import { addKeysToArray } from "../../utils/utilities";
import round from "../../utils/round";
import { getDateTimeNow, edit } from "../../utils/form_utilities";
import isEmpty from "../../validation/is-empty";
import classnames from "classnames";
import { useParams } from "react-router-dom";
import {
  DELIVERY_TYPE_DELIVERED_BY_SUPPLIER,
  DELIVERY_TYPE_PICKUP_BY_CUSTOMER,
} from "../../utils/constants";
import simpleMathFunction from "../../utils/simpleMathFunction";

const { Content } = Layout;

const title = "PROJECT ESTIMATE";
const url = "/api/project-estimates/";
const date_fields = ["date", "deadline"];

const records_column = [
  {
    title: "Category",
    dataIndex: "category",
  },
  {
    title: "Item",
    dataIndex: ["stock", "name"],
  },

  {
    title: "Qty",
    dataIndex: "quantity",
    align: "center",
    width: 80,
    render: (value, record) => (
      <span>{`${numberFormat(record.quantity)}`}</span>
    ),
  },

  {
    title: "UNIT",
    dataIndex: ["stock", "unit_of_measure"],
    align: "center",
    width: 100,
  },

  {
    title: "Unit Cost",
    dataIndex: "price",
    align: "center",
    width: 80,
    render: (value, record) => (
      <span>{record.footer !== 1 && `${numberFormat(record.price)}`}</span>
    ),
  },
  {
    title: "Amount",
    dataIndex: "amount",
    align: "right",
    width: 80,
    render: (value) => <span>{numberFormat(value)}</span>,
  },
  {
    title: "PO QTY",
    dataIndex: ["po_quantity"],
    align: "center",
    width: 100,
  },
  {
    title: "WW QTY",
    dataIndex: ["withdrawn_quantity"],
    align: "center",
    width: 100,
  },
];

export default function ProjectEstimatePrintout({
  match,
  reprint = false,
  duplicate = false,
}) {
  const auth = useSelector((state) => state.auth);

  const initialValues = {
    _id: null,
    po_no: null,
    date: moment(),
    supplier: null,
    remarks: "",
    items: [],

    total_amount: 0,
    total_advance_payment: 0,
    total_creditable_advance_payment: 0,
    total_credit_advance_payment: 0,
  };

  const [state, setState] = useState(initialValues);
  const [date, setDate] = useState(null);
  const [dr_numbers, setDrNumbers] = useState([]);

  const params = useParams();

  const total_estimated_material_cost = sumBy(state.items, (o) =>
    round(o.amount)
  );

  const total_estimated_mobilization_cost = simpleMathFunction(
    [
      state.estimated_meals,
      state.estimated_lodging,
      state.estimated_fare_cost,
      state.estimated_car_rentals_cost,
      state.estimated_miscellaneous,
      state.estimated_diesel_cost,
    ],
    "addition"
  );

  const total_estimated_labor_cost = simpleMathFunction(
    [
      state.average_daily_rate,
      state.estimated_production_crew,
      state.estimated_days,
    ],
    "multiplication"
  );

  const total_mat_lab_mob = simpleMathFunction(
    [
      total_estimated_labor_cost,
      total_estimated_mobilization_cost,
      total_estimated_material_cost,
    ],
    "addition"
  );

  const contingency = simpleMathFunction(
    [total_mat_lab_mob, state.contingency_rate / 100],
    "multiplication"
  );

  const taxes = useMemo(() => {
    const contract_price = state?.project?.project_price || 0;
    const total_project_cost =
      total_estimated_labor_cost +
      total_estimated_mobilization_cost +
      total_estimated_material_cost +
      contingency;

    const srp =
      total_project_cost + total_project_cost * (state.srp_rate / 100);

    if (!state?.project?.is_taxable) {
      return {
        name: "(NON VAT)",
        contract_price,
        total_project_cost,
        srp,
        net_of_vat: contract_price,
        net_income: contract_price - total_project_cost,
        vat: 0,
      };
    } else {
      if (state?.project?.is_vat_exclusive) {
        return {
          name: "(VAT EXCLUSIVE)",
          contract_price,
          total_project_cost,
          srp,
          net_of_vat: contract_price,
          net_income: contract_price - total_project_cost,
          vat: contract_price * 0.12,
        };
      } else {
        const net_of_vat = contract_price / 1.12;
        return {
          name: "(VAT INCLUSIVE)",
          contract_price,
          total_project_cost,
          srp,
          net_of_vat,
          net_income: net_of_vat - total_project_cost,
          vat: contract_price - contract_price / 1.12,
        };
      }
    }
  }, [state]);

  useEffect(() => {
    window.addEventListener("afterprint", (event) => {
      axios
        .post(`${url}${params.id}/print-status`, {
          user: auth.user,
        })
        .then((response) => {
          edit({
            record: { _id: response.data._id },
            setState,
            url,
            date_fields,
          });
        });
    });

    getDateTimeNow()
      .then((date) => setDate(date))
      .catch((err) =>
        message.error("There was an error getting curent date/time")
      );

    edit({
      record: { _id: params.id },
      setState,
      url,
      date_fields,
    });

    return () => {};
  }, []);

  useEffect(() => {
    let drs = [];

    if (state.sales_return?.items?.length > 0) {
      drs = state.sales_return.items.map((o) => o?.sale?.sales_no);
    }

    setDrNumbers(uniq(drs));
    return () => {};
  }, [state.sales_return, setDrNumbers]);

  return (
    <Content
      className={classnames("inventory-print print", {
        "m-t-4": duplicate,
      })}
    >
      <div>
        <Row>
          <Col span={20}>
            <ReportHeading has_logo={true} has_business_name={true} />
          </Col>
          <Col span={4}>
            {(!isEmpty(state.printed) || reprint) && (
              <div className="has-text-right has-text-weight-bold">REPRINT</div>
            )}
          </Col>
        </Row>

        <div className="has-text-centered is-size-5">{title}</div>
        <Row className="m-t-1">
          <Col span={4} className="is-flex align-items-flex-end">
            Date:
          </Col>
          <Col span={8} className="is-flex align-items-flex-end">
            {moment(state.date).format("ll")}
          </Col>
          <Col span={4} className="is-flex align-items-flex-end">
            EST#:
          </Col>
          <Col span={8} className="is-flex align-items-flex-end">
            <span className="ref-number">{state.po_no}</span>
          </Col>
        </Row>
        <Row>
          <Col key="label" span={4}>
            Project:
          </Col>
          <Col key="value" span={8} className="report-customer-name">
            {state.project?.name}
          </Col>
          <Col key="label" span={4}>
            Account:
          </Col>
          <Col key="value" span={8}>
            {state.project?.account?.name}
          </Col>
        </Row>

        <Row>
          <Col key="label" span={4}>
            Taxable:
          </Col>
          <Col key="value" span={8}>
            {state.project?.is_taxable ? "Yes" : "No"}
          </Col>
          <Col key="label" span={4}>
            VAT EX:
          </Col>
          <Col key="value" span={8}>
            {state.project?.is_vat_exclusive ? "Yes" : "No"}
          </Col>
        </Row>

        <Row>
          <Col key="label" span={4}>
            Deadline:
          </Col>
          <Col key="value" span={8}>
            {moment(state.deadline).format("ll")}
          </Col>
          <Col key="label" span={4}>
            Sales Division:
          </Col>
          <Col key="value" span={8}>
            {state.sales_division}
          </Col>
        </Row>

        <Row>
          <Col key="label" span={4}>
            Account Manager:
          </Col>
          <Col key="value" span={8}>
            {state.account_manager}
          </Col>
          <Col key="label" span={4}>
            Sales Division:
          </Col>
          <Col key="value" span={8}>
            {state.sales_division}
          </Col>
        </Row>

        <Row>
          <Col key="label" span={4}>
            Artist in Charge:
          </Col>
          <Col key="value" span={8}>
            {state.artist_in_charge}
          </Col>
          <Col key="label" span={4}>
            {"Rough Size (Width, Height, Area):"}
          </Col>
          <Col key="value" span={8}>
            {`${state.rough_sizes_width || 0} x ${
              state.rough_sizes_height || 0
            } x ${state.total_area || 0}`}
          </Col>
        </Row>

        <Row>
          <Col span={4}>Remarks</Col>
          <Col span={8} className="pre-wrap">
            {state.remarks}
          </Col>
          <Col span={4}>Printed:</Col>
          <Col span={8}>{date && moment(date).format("lll")}</Col>
        </Row>
      </div>

      <div className="m-t-1">
        MATERIAL COST ESTIMATE
        <Table
          size="small"
          dataSource={addKeysToArray([
            ...(state.items || []),
            {
              footer: 1,

              case_quantity: sumBy(state.items, (o) => round(o.case_quantity)),
              quantity: sumBy(state.items, (o) => round(o.quantity)),

              amount: sumBy(state.items, (o) => round(o.amount)),
            },
          ])}
          columns={records_column}
          rowKey={(item) => item._id}
          pagination={false}
          rowClassName={(record, index) => {
            if (record.footer === 1) {
              return "footer-summary has-text-weight-bold";
            }
          }}
        />
      </div>
      <div className="m-t-1">
        ESTIMATED MOBILIZATION COST
        <Row>
          <Col span={5} style={{ borderTop: "1px solid #000" }}>
            MEALS
          </Col>
          <Col style={{ borderTop: "1px solid #000" }}>
            {state.estimated_meals}
          </Col>
        </Row>
        <Row>
          <Col span={5}>LODGING</Col>
          <Col>{state.estimated_lodging}</Col>
        </Row>
        <Row>
          <Col span={5}>FARE COST</Col>
          <Col>{state.estimated_fare_cost}</Col>
        </Row>
        <Row>
          <Col span={5}>CAR RENTALS</Col>
          <Col>{state.estimated_car_rentals_cost}</Col>
        </Row>
        <Row>
          <Col span={5}>MISCELLANEOUS</Col>
          <Col>{state.estimated_miscellaneous}</Col>
        </Row>
        <Row>
          <Col span={5}>Estimated Diesel Cost</Col>
          <Col>{state.estimated_diesel_cost}</Col>
        </Row>
        <Row className="has-text-weight-bold">
          <Col span={5} style={{ borderTop: "1px solid #000" }}>
            TOTAL
          </Col>
          <Col style={{ borderTop: "1px solid #000" }}>
            {state.estimated_diesel_cost}
          </Col>
        </Row>
      </div>

      <div className="m-t-1">
        CONTINGENCY RATE
        <Row>
          <Col span={5} style={{ borderTop: "1px solid #000" }}>
            {`RATE PERCENTAGE (${state.contingency_rate}%)`}
          </Col>
          <Col
            className="has-text-weight-bold"
            style={{ borderTop: "1px solid #000" }}
          >
            {numberFormat(contingency)}
          </Col>
        </Row>
      </div>

      <div className="m-t-1">
        TAXES
        <Row>
          <Col span={5} style={{ borderTop: "1px solid #000" }}>
            CONTRACT PRICE {taxes.name}:
          </Col>
          <Col style={{ borderTop: "1px solid #000" }}>
            {numberFormat(state.project?.project_price)}
          </Col>
        </Row>
        <Row>
          <Col span={5}>NET OF VAT:</Col>
          <Col>{numberFormat(taxes.net_of_vat)}</Col>
        </Row>
        <Row>
          <Col span={5}>{"VAT (12%)"}</Col>
          <Col>{numberFormat(taxes.vat)}</Col>
        </Row>
        <Row>
          <Col span={5}>SRP {state.srp_rate}%</Col>
          <Col>{numberFormat(taxes.srp)}</Col>
        </Row>
        <Row>
          <Col span={5}>NET INCOME:</Col>
          <Col>{numberFormat(taxes.net_income)}</Col>
        </Row>
        <Row className="has-text-weight-bold">
          <Col span={4} style={{ borderTop: "1px solid #000" }}>
            TOTAL Margin and Cost
          </Col>
          <Col span={1} style={{ borderTop: "1px solid #000" }}>
            {round(taxes.net_income / taxes.net_of_vat)}%
          </Col>
          <Col span={1} style={{ borderTop: "1px solid #000" }}>
            {numberFormat(taxes.total_project_cost)}
          </Col>
        </Row>
      </div>

      <div className="signatories-container">
        <Row gutter={48}>
          <Col span={8}>Prepared by</Col>
          <Col span={8}>Checked by</Col>
          <Col span={8}>Approved by</Col>
        </Row>
        <Row gutter={48}>
          <Col span={8}>
            <div className="signatory">{state.logs?.[0]?.user?.name}</div>
          </Col>
          <Col span={8} className="has-text-centered">
            <div className="signatory">&nbsp;</div>
          </Col>
          <Col span={8}>
            <div className="signatory">&nbsp;</div>
          </Col>
        </Row>
      </div>
    </Content>
  );
}
